import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import Navbar from "../components/Navbar";
import api from "../services/api";
import {
  Package,
  Truck,
  Shield,
  Clock,
  MapPin,
  ArrowRight,
  CheckCircle2,
  Zap,
  Star,
  GraduationCap,
  Store,
  Briefcase,
  Home as HomeIcon,
  Luggage,
  Building2,
  Sofa,
  CheckCircle,
  Mail,
  Phone,
  Wallet,
  Crown,
  Gift,
  Tag,
  Box,
  Users,
  Percent,
  Sparkles,
  Check,
} from "lucide-react";
import Logo from "../components/Logo";
import AIScannerSection from "../components/AIScannerSection";
import PricingCalculator from "../components/PricingCalculator";
import CustomerReviews from "../components/CustomerReviews";
import HomeFAQ from "../components/HomeFAQ";
import HeroGoogleSignIn from "../components/HeroGoogleSignIn";
import useAuthStore from "../store/authStore";

const features = [
  {
    icon: Truck,
    title: "Real-Time Tracking",
    description: "Track your deliveries in real-time with live location updates and ETAs.",
  },
  {
    icon: Shield,
    title: "Secure Handling",
    description: "Your packages are insured and handled with professional care at every step.",
  },
  {
    icon: Clock,
    title: "On-Time Delivery",
    description: "99.2% on-time delivery rate. We value your time as much as you do.",
  },
  {
    icon: MapPin,
    title: "Wide Coverage",
    description: null, // Dynamically set using liveStats
  },
];

const steps = [
  { step: "01", title: "Book from Website", description: "Visit pickngo.co.in. Enter pickup & delivery details, weight and get instant options." },
  { step: "02", title: "Compare", description: "Compare live prices from top couriers and choose the best option." },
  { step: "03", title: "Do Payment", description: "Make secure online payment quickly and easily." },
  { step: "04", title: "We Pick Up", description: "Our gig agents pick up the parcel from your doorstep." },
  { step: "05", title: "We Pack", description: "We pack your parcel safely and ship it via the best courier." },
  { step: "06", title: "We Deliver Safely & Take with OTP", description: "We deliver your parcel safely to your doorstep and take with OTP for complete security." },
];

const useCases = [
  {
    icon: GraduationCap,
    badge: "MOST ACTIVE",
    badgeColor: "text-blue-600 bg-blue-50",
    borderColor: "border-blue-100",
    title: "Students Shifting Rooms",
    description: "Moving between hostels or sending items home during vacations? PickNGo handles the packing, pickup, and delivery so you can focus on what matters.",
  },
  {
    icon: Store,
    badge: "HIGHEST VOLUME",
    badgeColor: "text-[#FF6B00] bg-orange-50",
    borderColor: "border-orange-100",
    title: "Instagram Sellers",
    description: "Ship 10 or 100 orders a day with professional packing. Give your customers a premium unboxing experience without the logistics headache.",
  },
  {
    icon: Briefcase,
    badge: "TIME SAVERS",
    badgeColor: "text-emerald-600 bg-emerald-50",
    borderColor: "border-emerald-100",
    title: "Working Professionals",
    description: "Too busy to visit a courier shop? Schedule a pickup from your office or home. Our agent handles everything while you stay productive.",
  },
];

const shiftingServices = [
  {
    icon: HomeIcon,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    title: "Room Shifting",
    description: "Moving from hostel, PG, or apartment? We pack everything, load it up, and deliver to your new place safely.",
    features: ["Packing included", "Same-city delivery", "Furniture handling"],
  },
  {
    icon: Luggage,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    title: "Luggage Shifting",
    description: "Heading to the airport or railway station? We pick up your luggage and deliver it wherever you need — hassle-free.",
    features: ["Airport/station transfers", "Secure handling", "On-time guarantee"],
  },
  {
    icon: Building2,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    title: "Office Shifting",
    description: "Relocating your workspace? We handle desks, chairs, electronics, and files with professional care.",
    features: ["IT equipment care", "Bulk handling", "Weekend slots available"],
  },
  {
    icon: Sofa,
    iconBg: "bg-orange-50",
    iconColor: "text-[#FF6B00]",
    title: "Small Furniture",
    description: "Need to move a table, mattress, or appliance? No need to hire a full truck — we handle single-item moves too.",
    features: ["Single-item moves", "Bubble wrap protection", "Doorstep to doorstep"],
  },
];

function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M+`;
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}K+`;
  return `${num}+`;
}

function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  const currentRef = useRef(0);

  useEffect(() => {
    if (target === 0) return;
    const start = currentRef.current;
    const diff = target - start;
    if (diff === 0) return;
    const steps = Math.max(Math.ceil(duration / 16), 1);
    const increment = diff / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      if (step >= steps) {
        currentRef.current = target;
        setCount(target);
        clearInterval(timer);
      } else {
        currentRef.current = start + increment * step;
        setCount(Math.round(currentRef.current));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);

  return count;
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [liveStats, setLiveStats] = useState(null);
  // Admin-editable contact info (email / phone / WhatsApp / Instagram).
  // Drives footer contact section; changes from /dashboard/contact-settings
  // reflect live on next page load without redeploy.
  const [siteSettings, setSiteSettings] = useState(null);
  const visitRecorded = useRef(false);

  useEffect(() => {
    api.get("/site-settings")
      .then(({ data }) => setSiteSettings(data))
      .catch(() => { /* fallback values hardcoded in JSX */ });
  }, []);

  // Typewriter effect — rotating hero headlines
  const typingTexts = [
    "Ship It. Forget It. We've Got You.",
    "From Pickup to Doorstep — India's Fastest Promise.",
    "Grow Faster. Ship Smarter. Pay Less.",
    "Every Corner of India. One Click Away.",
  ];
  const [displayText, setDisplayText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = typingTexts[textIndex];
    const speed = isDeleting ? 30 : 60;

    if (!isDeleting && charIndex === current.length) {
      const pause = setTimeout(() => setIsDeleting(true), 2000);
      return () => clearTimeout(pause);
    }

    if (isDeleting && charIndex === 0) {
      setIsDeleting(false);
      setTextIndex((prev) => (prev + 1) % typingTexts.length);
      return;
    }

    const timer = setTimeout(() => {
      setCharIndex((prev) => prev + (isDeleting ? -1 : 1));
      setDisplayText(current.substring(0, charIndex + (isDeleting ? -1 : 1)));
    }, speed);

    return () => clearTimeout(timer);
  }, [charIndex, isDeleting, textIndex]);

  useEffect(() => {
    // Initial fetch + record visit
    api.get("/public/stats").then(({ data }) => setLiveStats(data)).catch(() => {});
    if (!visitRecorded.current) {
      visitRecorded.current = true;
      api.post("/public/visit").then(({ data }) => {
        setLiveStats((prev) => prev ? { ...prev, total_customers: data.total_customers } : prev);
      }).catch(() => {});
    }
    // Poll every 5 seconds for live updates
    const interval = setInterval(() => {
      api.get("/public/stats").then(({ data }) => setLiveStats(data)).catch(() => {});
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const deliveriesCount = useCountUp(liveStats?.deliveries_completed ?? 0);
  const agentsCount = useCountUp(liveStats?.active_agents ?? 0);
  const citiesCount = useCountUp(liveStats?.cities_covered ?? 0);
  const customersCount = useCountUp(liveStats?.total_customers ?? 0);
  // Combined platform headcount = customers + agents — surfaced on the hero
  // "Trusted by N+ users" pill so the number reflects the entire community
  // signed up to PickNGo, not just the customer side.
  const totalUsersRaw = (liveStats?.total_customers ?? 0) + (liveStats?.active_agents ?? 0);
  const totalUsersCount = useCountUp(totalUsersRaw);

  const stats = [
    { value: liveStats ? `${citiesCount}` : "...", label: "Cities Covered" },
    { value: liveStats ? `${agentsCount}+` : "...", label: "Delivery Agents" },
    { value: liveStats ? `${deliveriesCount}+` : "...", label: "Deliveries Completed" },
    { value: liveStats ? `${liveStats.on_time_rate}%` : "...", label: "On-Time Delivery" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="home-page">
      <Navbar />

      {/* Hero */}
      <section id="hero" className="relative overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl animate-fadeInUp">
              <div className="inline-flex items-center gap-2 bg-orange-50 text-[#FF6B00] px-4 py-2 rounded-full text-sm font-medium mb-6 border border-orange-100">
                <Zap className="h-4 w-4" />
                <span>B2B & B2C & D2C Express Logistics</span>
              </div>
              <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-slate-900 leading-[1.1] mb-3 min-h-[8rem] sm:min-h-[9rem] lg:min-h-[11.5rem]" data-testid="hero-heading">
                <span className="text-slate-900 typing-cursor">{displayText}</span>
              </h1>
              <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8 max-w-md">
                Express B2B, B2C &amp; D2C logistics for manufacturers, distributors &amp; retailers. Live courier comparison, AI-powered pricing and a <span className="font-semibold text-slate-900">₹50 credit</span> if we miss your delivery window.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => navigate("/register")}
                  className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-8 py-3 font-medium shadow-sm shadow-[#FF6B00]/20 transition-all text-base h-auto"
                  data-testid="hero-get-started-btn"
                >
                  Start Free Pilot
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/b2b")}
                  className="bg-white text-slate-700 hover:bg-slate-50 border border-slate-200 rounded-xl px-8 py-3 font-medium transition-all text-base h-auto"
                  data-testid="hero-b2b-pricing-btn"
                >
                  View B2B Pricing
                </Button>
              </div>
              <HeroGoogleSignIn />
              <div className="flex items-center gap-6 mt-8">
                <div className="flex -space-x-2">
                  {["R", "P", "A", "S"].map((letter, i) => (
                    <div
                      key={`avatar-${letter}`}
                      className="w-9 h-9 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: ["#FF6B00", "#10B981", "#F59E0B", "#8B5CF6"][i] }}
                    >
                      {letter}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`hero-star-${i}`} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  {/* Show the live customers+agents combined count once it crosses 10.
                      Below that, pitch the service as launch-phase with "We are coming
                      soon" so small numbers don't undermine credibility. Admin can
                      manually override the combined count from /dashboard/stats (adds
                      to the real DB count, so real sign-ups still grow it). */}
                  <p className="text-xs text-slate-500 mt-0.5" data-testid="trusted-by-pill">
                    {liveStats && totalUsersRaw > 10
                      ? `Trusted by ${formatNumber(totalUsersCount)} users`
                      : "We are coming soon"}
                  </p>
                </div>
              </div>
            </div>
            <div className="hidden lg:flex justify-center">
              <div className="w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl relative" data-testid="hero-video">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  poster="https://static.prod-images.emergentagent.com/jobs/3b8aba2d-d6b4-41ec-8574-6a0479266030/images/b8e1ac6bf7d834024afcf14c8166e801649b7c7140e5c68038067afa14482d68.png"
                  className="w-full h-auto object-cover rounded-2xl"
                  onCanPlay={(e) => e.target.play().catch(() => {})}
                >
                  <source src="/delivery-steps.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </div>
        {/* Decorative gradient blob */}
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-orange-100 rounded-full blur-3xl opacity-40 pointer-events-none" />
      </section>

      {/* Stats */}
      <section className="border-y border-slate-200 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="font-['Outfit'] text-3xl md:text-4xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-sm text-slate-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 md:py-28" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] mb-3">Why PickNGo</p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold text-slate-900 mb-4">
              Built for speed and reliability
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Everything you need to move packages across the city with confidence.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 hover:shadow-lg hover:-translate-y-1 hover:border-[#FF6B00]/50 transition-all duration-300 group"
                data-testid={`feature-card-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-12 h-12 bg-slate-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FF6B00] transition-colors duration-300">
                  <feature.icon className="h-6 w-6 text-slate-600 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {feature.description || `Serving ${liveStats ? `${citiesCount}+` : "2+"} cities with a network of ${liveStats ? `${agentsCount}+` : "4+"} verified delivery agents.`}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-20 md:py-28 bg-white" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] mb-3">How It Works</p>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-semibold text-slate-900">
              From booking to doorstep — 6 simple steps
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 max-w-5xl mx-auto">
            {steps.map((item, i) => (
              <div key={item.step} className="relative flex flex-col items-center text-center" data-testid={`step-${item.step}`}>
                <div className="w-14 h-14 bg-[#FF6B00] rounded-2xl flex items-center justify-center mb-5 shadow-sm shadow-[#FF6B00]/20">
                  <span className="font-['Outfit'] text-xl font-bold text-white">{item.step}</span>
                </div>
                <h3 className="font-['Outfit'] text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-xs">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Scanner */}
      <AIScannerSection />

      {/* Use Cases - Built for Everyone */}
      <section id="pricing" className="py-20 md:py-28" data-testid="use-cases-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
              Use Cases
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Built for <span className="text-[#FF6B00]">Everyone</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Whether you're shipping one package or a hundred, PickNGo adapts to your needs.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {useCases.map((uc) => (
              <div
                key={uc.title}
                className={`bg-white rounded-2xl border ${uc.borderColor} p-8 hover:shadow-lg hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:bg-gradient-to-br hover:from-[#FF6B00]/[0.03] hover:to-transparent transition-all duration-300 group relative`}
                data-testid={`usecase-card-${uc.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className={`absolute top-6 right-6 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${uc.badgeColor}`}>
                  {uc.badge}
                </span>
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#FF6B00] transition-colors duration-300">
                  <uc.icon className="h-6 w-6 text-slate-700 group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-3">{uc.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">{uc.description}</p>
                <button
                  onClick={() => navigate("/register")}
                  className="text-sm font-semibold text-[#FF6B00] hover:text-[#E65A00] transition-colors flex items-center gap-1 group/link"
                >
                  Learn more
                  <ArrowRight className="h-4 w-4 group-hover/link:translate-x-0.5 transition-transform" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Room & Luggage Shifting */}
      <section id="shifting" className="py-20 md:py-28 bg-white" data-testid="shifting-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
              <Truck className="h-3.5 w-3.5" />
              New Service
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Room & Luggage <span className="text-[#FF6B00]">Shifting</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Moving rooms, shifting offices, or transferring luggage? PickNGo handles it all — from packing to delivery.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {shiftingServices.map((svc) => (
              <div
                key={svc.title}
                className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-lg hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:bg-gradient-to-br hover:from-[#FF6B00]/[0.03] hover:to-transparent transition-all duration-300 group"
                data-testid={`shifting-card-${svc.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`w-12 h-12 ${svc.iconBg} rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#FF6B00] transition-colors duration-300`}>
                  <svc.icon className={`h-6 w-6 ${svc.iconColor} group-hover:text-white transition-colors duration-300`} />
                </div>
                <h3 className="font-['Outfit'] text-lg font-bold text-slate-900 mb-2">{svc.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">{svc.description}</p>
                <ul className="space-y-2">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-[#FF6B00] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Express Logistics Banner */}
      <section className="py-16 md:py-20 bg-[#0B132B] relative overflow-hidden" data-testid="b2b-express-banner">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/10 to-transparent pointer-events-none" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#FF6B00]/5 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            {/* Left — Text */}
            <div>
              <span className="inline-flex items-center gap-2 bg-[#FF6B00]/20 text-[#FF6B00] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-5">
                <Zap className="h-3.5 w-3.5" /> B2B & B2C & D2C Express Logistics
              </span>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-4">
                Reliable Tier 2/3 Delivery<br/>with <span className="text-[#FF6B00]">SLA Guarantee</span>
                <span
                  className="inline-flex items-center gap-1.5 ml-3 align-middle text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-slate-900 px-2.5 py-1 rounded-full shadow-sm animate-pulse"
                  data-testid="b2b-coming-soon-badge"
                >
                  <span className="w-1.5 h-1.5 bg-slate-900 rounded-full" />
                  Coming Soon
                </span>
              </h2>
              <p className="text-base text-slate-300 leading-relaxed mb-6 max-w-lg">
                Express B2B logistics for manufacturers, distributors & MSMEs. Base + per-kg pricing.
                Volume discounts up to 35%. ₹50 credit if we miss your delivery window.
              </p>
              <div className="flex flex-wrap gap-3 mb-8">
                <Button onClick={() => navigate("/b2b")} className="bg-[#FF6B00] hover:bg-[#E65A00] text-white rounded-xl h-12 px-8 text-base gap-2 shadow-lg shadow-[#FF6B00]/20" data-testid="b2b-banner-cta">
                  View B2B Pricing <ArrowRight className="h-4 w-4" />
                </Button>
                <Button onClick={() => navigate("/b2b")} variant="outline" className="border-slate-500 text-white hover:bg-white/10 rounded-xl h-12 px-6 text-base" data-testid="b2b-banner-demo">
                  Request Demo
                </Button>
              </div>
              {/* Trust badges */}
              <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> 95% On-Time Delivery</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> OTP-Verified</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Real-Time GPS</span>
              </div>
            </div>

            {/* Right — Key Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 bg-[#FF6B00]/20 rounded-xl flex items-center justify-center mb-3">
                  <Clock className="h-5 w-5 text-[#FF6B00]" />
                </div>
                <p className="font-['Outfit'] text-2xl font-extrabold text-white">1-2 Day</p>
                <p className="text-sm text-slate-400 mt-1">Delivery SLA</p>
                <p className="text-xs text-slate-500 mt-2">Tier 1: Next-day | Tier 2/3: 2-day</p>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="h-5 w-5 text-emerald-400" />
                </div>
                <p className="font-['Outfit'] text-2xl font-extrabold text-white">₹50 Credit</p>
                <p className="text-sm text-slate-400 mt-1">SLA Penalty Guarantee</p>
                <p className="text-xs text-slate-500 mt-2">Auto-credited if we miss your window</p>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center mb-3">
                  <Tag className="h-5 w-5 text-blue-400" />
                </div>
                <p className="font-['Outfit'] text-2xl font-extrabold text-white">From ₹52</p>
                <p className="text-sm text-slate-400 mt-1">Per Shipment</p>
                <p className="text-xs text-slate-500 mt-2">Volume discounts for 1000+ shipments/mo</p>
              </div>
              <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
                <div className="w-10 h-10 bg-violet-500/20 rounded-xl flex items-center justify-center mb-3">
                  <Truck className="h-5 w-5 text-violet-400" />
                </div>
                <p className="font-['Outfit'] text-2xl font-extrabold text-white">Tier 2/3</p>
                <p className="text-sm text-slate-400 mt-1">City Coverage</p>
                <p className="text-xs text-slate-500 mt-2">Where other players struggle, we deliver</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* B2B Business Section */}
      <section id="business" className="py-20 md:py-28" data-testid="b2b-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
              <Briefcase className="h-3.5 w-3.5" />
              For Business
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              B2B Wallet — <span className="text-[#FF6B00]">Ship Smarter, Save More</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Prepaid wallet plans for businesses. Get bonus credits, flat-rate pricing, and priority pickups — all from a single dashboard.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                tier: "Bronze",
                price: "5,000",
                color: "#CD7F32",
                bg: "bg-amber-50",
                border: "border-amber-200",
                features: ["+5% bonus credits on every recharge", "Dedicated wallet dashboard", "All payment methods supported"],
                highlight: false,
              },
              {
                tier: "Silver",
                price: "15,000",
                color: "#64748B",
                bg: "bg-slate-50",
                border: "border-slate-300",
                features: ["10% discount on all base delivery fees", "1 free fragile packaging per month", "Priority support"],
                highlight: true,
              },
              {
                tier: "Gold",
                price: "50,000",
                color: "#F59E0B",
                bg: "bg-yellow-50",
                border: "border-yellow-300",
                features: ["Flat rate for first 3 km deliveries", "Priority pickup within 15 minutes", "Volume discounts + dedicated account manager"],
                highlight: false,
              },
            ].map((plan) => (
              <div
                key={plan.tier}
                className={`relative rounded-2xl border-2 ${plan.highlight ? `${plan.border} ${plan.bg} shadow-lg scale-[1.03]` : `border-slate-100 bg-white`} p-7 transition-all hover:shadow-lg hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:bg-gradient-to-br hover:from-[#FF6B00]/[0.03] hover:to-transparent duration-300 group`}
                data-testid={`b2b-plan-${plan.tier.toLowerCase()}`}
              >
                {plan.highlight && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF6B00] text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-wider">
                    Popular
                  </span>
                )}
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${plan.color}15` }}>
                    <Crown className="h-5 w-5" style={{ color: plan.color }} />
                  </div>
                  <span className="font-['Outfit'] text-lg font-bold text-slate-900">{plan.tier}</span>
                </div>
                <p className="font-['Outfit'] text-3xl font-bold text-slate-900 mb-1">
                  INR {plan.price}
                </p>
                <p className="text-xs text-slate-400 mb-5">one-time recharge</p>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-[#FF6B00] shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate("/b2b")}
                  className={`w-full rounded-xl h-11 font-semibold ${
                    plan.highlight
                      ? "bg-[#FF6B00] text-white hover:bg-[#E65A00]"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`b2b-cta-${plan.tier.toLowerCase()}`}
                >
                  Get Started
                  <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2C Micro-Shift Bundles */}
      <section id="bundles" className="py-20 md:py-28 bg-white" data-testid="bundles-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
              <Box className="h-3.5 w-3.5" />
              Micro-Shifting
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Fixed-Price <span className="text-[#FF6B00]">Moving Bundles</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              No surprises. Pick a bundle that fits your move and pay a flat price — packing, loading, and delivery all included.
            </p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: "Studio Move",
                items: "Up to 10 large boxes",
                price: "1,999",
                desc: "Perfect for studio apartments — a few boxes, a suitcase, and essentials.",
                icon: Package,
                color: "text-blue-600",
                bg: "bg-blue-50",
              },
              {
                title: "1RK Move",
                items: "Up to 20 items",
                price: "3,499",
                desc: "Bed, table, wardrobe, and kitchen basics — everything for a 1RK.",
                icon: HomeIcon,
                color: "text-purple-600",
                bg: "bg-purple-50",
              },
              {
                title: "1BHK Move",
                items: "Up to 40 items",
                price: "5,999",
                desc: "Full 1BHK — furniture, appliances, boxes. Our crew handles it all.",
                icon: Building2,
                color: "text-emerald-600",
                bg: "bg-emerald-50",
              },
            ].map((bundle) => (
              <div
                key={bundle.title}
                className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-lg hover:-translate-y-1 hover:border-[#FF6B00]/40 hover:bg-gradient-to-br hover:from-[#FF6B00]/[0.03] hover:to-transparent transition-all duration-300 group"
                data-testid={`bundle-card-${bundle.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className={`w-12 h-12 ${bundle.bg} rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#FF6B00] transition-colors duration-300`}>
                  <bundle.icon className={`h-6 w-6 ${bundle.color} group-hover:text-white transition-colors duration-300`} />
                </div>
                <h3 className="font-['Outfit'] text-xl font-bold text-slate-900 mb-1">{bundle.title}</h3>
                <p className="text-xs text-slate-400 mb-3">{bundle.items}</p>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">{bundle.desc}</p>
                <div className="flex items-end gap-1 mb-5">
                  <span className="font-['Outfit'] text-3xl font-bold text-slate-900">INR {bundle.price}</span>
                  <span className="text-xs text-slate-400 mb-1">starting</span>
                </div>
                <ul className="space-y-2 mb-5">
                  {["Packing included", "Loading & unloading", "Doorstep delivery"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-3.5 w-3.5 text-[#FF6B00] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => navigate("/register")}
                  className="w-full rounded-xl h-11 font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  Book Now <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              </div>
            ))}
          </div>
          {/* Volume Reward Callout */}
          <div className="max-w-3xl mx-auto mt-10">
            <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-2xl px-6 py-4" data-testid="volume-reward-banner">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
                <Gift className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-emerald-800">Book 5+ items and earn a 20% coupon!</p>
                <p className="text-xs text-emerald-600">Every micro-shift with 5 or more items gets you a 20% discount coupon for your next courier booking.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Smart Savings Section */}
      <section id="savings" className="py-20 md:py-28" data-testid="savings-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.25em] text-white bg-gradient-to-r from-[#FF6B00] to-[#FF9A3C] px-4 py-1.5 rounded-full mb-3 shadow-sm shadow-[#FF6B00]/25" data-testid="coming-soon-badge">
              <Zap className="h-3 w-3" />
              Coming Soon
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100 ml-0 md:ml-2">
              <Percent className="h-3.5 w-3.5" />
              Smart Savings
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Deliver <span className="text-[#FF6B00]">Faster</span> — B2B & B2C & D2C
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              1-day delivery in metros, 2-day to Tier 2/3 cities. Volume discounts for businesses, reliable speed for everyone.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* B2B Volume Discount */}
            <div className="bg-white rounded-2xl border border-orange-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden" data-testid="savings-b2b-volume">
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-orange-50 rounded-xl flex items-center justify-center">
                    <Briefcase className="h-6 w-6 text-[#FF6B00]" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">B2B Volume Pricing</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-[#FF6B00] bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200">
                      <Tag className="h-3 w-3" /> UP TO 35% OFF
                    </span>
                  </div>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  Ship more, pay less. B2B volume slabs from ₹52/shipment for 1000+ monthly orders across Tier 1/2/3 cities.
                </p>
                <ul className="space-y-2">
                  {["4 volume slabs with tiered pricing", "As low as ₹52 per shipment", "₹50 SLA penalty guarantee"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-[#FF6B00] shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button onClick={() => navigate("/b2b")} variant="outline" className="w-full mt-5 rounded-xl h-10 text-sm border-orange-200 text-[#FF6B00] hover:bg-orange-50 font-semibold" data-testid="savings-b2b-cta">
                  View B2B Pricing <ArrowRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              </div>
            </div>
            {/* 1-Day Metro Delivery */}
            <div className="bg-white rounded-2xl border border-blue-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden" data-testid="savings-1day-delivery">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <Zap className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">1-Day Metro Delivery</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200">
                      <Tag className="h-3 w-3" /> TIER 1 CITIES
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5 mb-4">
                  <span className="text-[9px] font-bold bg-orange-50 text-[#FF6B00] px-2 py-0.5 rounded-full border border-orange-200">B2B</span>
                  <span className="text-[9px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-200">B2C</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  Next-day delivery across metro cities — Mumbai, Delhi, Bengaluru, Chennai, Hyderabad & Pune. Speed you can count on.
                </p>
                <ul className="space-y-2">
                  {["Next-day delivery guaranteed", "From ₹45 base + ₹8/kg", "OTP-verified with live GPS tracking"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            {/* 2-Day Tier 2/3 Delivery */}
            <div className="bg-white rounded-2xl border border-emerald-100 p-7 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative overflow-hidden" data-testid="savings-2day-delivery">
              <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <Truck className="h-6 w-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-['Outfit'] text-lg font-bold text-slate-900">2-Day Tier 2/3 Delivery</h3>
                    <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                      <Tag className="h-3 w-3" /> 50+ CITIES
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5 mb-4">
                  <span className="text-[9px] font-bold bg-orange-50 text-[#FF6B00] px-2 py-0.5 rounded-full border border-orange-200">B2B</span>
                  <span className="text-[9px] font-bold bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full border border-blue-200">B2C</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  Reliable 2-day delivery to Tier 2/3 cities — Coimbatore, Surat, Jaipur, Nagpur, Kochi & more. Where others struggle, we deliver.
                </p>
                <ul className="space-y-2">
                  {["2-day delivery SLA guaranteed", "₹50 credit if we miss the window", "Hub-routed for reliability"].map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Calculator */}
      <section id="calculator" className="py-20 md:py-28 bg-white" data-testid="calculator-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#FF6B00] bg-orange-50 px-4 py-2 rounded-full mb-4 border border-orange-100">
              <Sparkles className="h-3.5 w-3.5" />
              Try It Now
            </span>
            <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-slate-900 mb-4">
              Get Your Price in <span className="text-[#FF6B00]">Seconds</span>
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              Enter your pickup and delivery locations. See the exact price, zone, ETA and available discounts — no account required.
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <PricingCalculator onGetStarted={() => navigate("/register")} />
          </div>
        </div>
      </section>

      {/* Customer Reviews */}
      <CustomerReviews isAdmin={user?.role === "admin"} />

      {/* FAQ — visible content matching the FAQPage JSON-LD in index.html */}
      <HomeFAQ />

      {/* CTA */}
      <section id="contact" className="py-20 md:py-28" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#0B132B] rounded-3xl p-10 md:p-16 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B00]/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-white mb-4">
                Ready to deliver?
              </h2>
              <p className="text-base text-slate-300 mb-8 max-w-md mx-auto">
                Join thousands of businesses and individuals who trust PickNGo for their delivery needs.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/register")}
                  className="bg-[#FF6B00] text-white hover:bg-[#E65A00] rounded-xl px-8 py-3 font-medium transition-all text-base h-auto"
                  data-testid="cta-get-started-btn"
                >
                  Start as Individual
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate("/b2b/login")}
                  className="bg-white/10 text-white hover:bg-white/20 border border-white/20 rounded-xl px-8 py-3 font-medium transition-all text-base h-auto"
                  data-testid="cta-business-btn"
                >
                  <Wallet className="h-5 w-5 mr-2" />
                  Start as Business
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/become-agent")}
                  className="bg-transparent text-white hover:bg-white/10 border border-white/20 rounded-xl px-8 py-3 font-medium transition-all text-base h-auto"
                  data-testid="cta-agent-btn"
                >
                  Become an Agent
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            {/* Brand */}
            <div>
              <Logo size="sm" />
              <p className="text-xs text-slate-500 mt-3 leading-relaxed">
                Same-city courier &amp; cross-city logistics aggregator. Live tracking, transparent pricing, 99% on-time.
              </p>
            </div>
            {/* Explore */}
            <div>
              <h4 className="font-['Outfit'] text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Explore</h4>
              <nav className="space-y-2.5" aria-label="Site sections">
                <a href="/b2b" className="block text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-b2b-link">B2B Express</a>
                <a href="/become-agent" className="block text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-agent-link">Become a Delivery Partner</a>
                <a href="/college" className="block text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-college-link">College Logistics</a>
                <a href="/login" className="block text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-login-link">Sign In</a>
                <a href="/register" className="block text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-register-link">Create Account</a>
              </nav>
            </div>
            {/* Contact */}
            <div>
              <h4 className="font-['Outfit'] text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Contact Us</h4>
              <div className="space-y-3">
                <a href={`mailto:${siteSettings?.contact_email || "pickngo13@gmail.com"}`} className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-email">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {siteSettings?.contact_email || "pickngo13@gmail.com"}
                </a>
                {/* Phone click → opens WhatsApp chat (admin preference) with default prefill.
                    Fallback to tel: dialer while settings are loading.
                    onClick = defensive popup-open so iframe sandboxes (preview) don't silently block the redirect. */}
                <a
                  href={
                    siteSettings?.whatsapp_number
                      ? `https://wa.me/${siteSettings.whatsapp_number.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(siteSettings.whatsapp_default_message || "Hi PickNGo!")}`
                      : `tel:${(siteSettings?.contact_phone || "+917995865243").replace(/\s/g, "")}`
                  }
                  target={siteSettings?.whatsapp_number ? "_blank" : undefined}
                  rel={siteSettings?.whatsapp_number ? "noopener noreferrer" : undefined}
                  onClick={(e) => {
                    if (!siteSettings?.whatsapp_number) return;
                    e.preventDefault();
                    const digits = siteSettings.whatsapp_number.replace(/[^0-9]/g, "");
                    const url = `https://wa.me/${digits}?text=${encodeURIComponent(siteSettings.whatsapp_default_message || "Hi PickNGo!")}`;
                    try {
                      const w = window.open(url, "_blank", "noopener,noreferrer");
                      if (!w) window.location.href = url;
                    } catch {
                      window.location.href = url;
                    }
                  }}
                  className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline"
                  data-testid="footer-phone"
                >
                  <Phone className="h-4 w-4 text-slate-400" />
                  {siteSettings?.contact_phone || "+91 7995865243"}
                </a>
              </div>
            </div>
            {/* Social */}
            <div>
              <h4 className="font-['Outfit'] text-sm font-bold text-slate-900 uppercase tracking-wider mb-4">Follow Us</h4>
              <a href={siteSettings?.instagram_url || "https://www.instagram.com/pick_n_go2026/"} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-instagram">
                <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                {siteSettings?.instagram_handle || "@pick_n_go2026"}
              </a>

              {/* App Store Coming Soon */}
              <div className="mt-5" data-testid="footer-app-stores">
                <p className="text-[10px] font-bold text-[#FF6B00] uppercase tracking-widest mb-3">Coming Soon</p>
                <div className="space-y-3">
                  <a href="#" onClick={(e) => e.preventDefault()} className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-play-store">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302a1 1 0 010 1.38l-2.302 2.302L15.2 12.69l2.498-3.182zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z"/></svg>
                    Google Play
                  </a>
                  <a href="#" onClick={(e) => e.preventDefault()} className="flex items-center gap-2.5 text-sm text-slate-600 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-app-store">
                    <svg className="h-4 w-4 text-slate-400" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
                    App Store
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-100 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-slate-400">© {new Date().getFullYear()} PickNGo. All rights reserved.</p>
            <div className="flex items-center gap-5">
              <a href="/status" className="text-xs text-slate-500 hover:text-[#FF6B00] transition-colors no-underline inline-flex items-center gap-1.5" data-testid="footer-status-link">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                System Status
              </a>
              <a href="/privacy" className="text-xs text-slate-500 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-privacy-link">Privacy Policy</a>
              <a href="/terms" className="text-xs text-slate-500 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-terms-link">Terms of Service</a>
              <a href="/admin/login" className="text-xs text-slate-400 hover:text-[#FF6B00] transition-colors no-underline" data-testid="footer-admin-link">Admin</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
